import { useState } from "react";
import { Link } from "react-router-dom";
import { TriangleAlert, GitCommitHorizontal, GitPullRequest, ShieldCheck, ScanSearch, History, ExternalLink, ChevronRight } from "lucide-react";
import { APP_NAME } from "@/config";
import { useAuth } from "@/context/AuthContext";
import { useFlags } from "@/lib/flags";
import { investigatePath } from "@/components/InvestigateTimeline";
import { Inline } from "@/lib/inline";
import { int, plural, Chip, Tag, shortDate } from "@/components/common";
import { kindOf, isDocuments, docName, docFolderLine, DOC_STATE, versionsOf, versionDetail } from "@/lib/workKinds";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// A session's change report, as the design handoff draws it (Objectives and Change Reports): built by the platform
// from git, never editable by the coder. The branch, file paths and commit SHAs keep the monospace (they're the diff).
export const FILE_STATUS = { added: "text-gr-success", modified: "text-gr-warning", deleted: "text-gr-danger" };
export const TRIGGER = { submit: "Submitted", end: "Ended", requested: "Built" };
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export const reportWhen = (r) => `${TRIGGER[r.trigger] || "Built"} ${shortDate(r.created_at, true)}`;

// "ai: claude-sonnet-5-5" or "none: MockEngine session"
export const summarySource = (s, docs) => (s || "").startsWith("ai:") ? (docs ? "Written by AI from the changes alone" : "Written by AI from the diff alone")
  : `No AI summary${s && s.includes(":") ? `: ${s.split(":").slice(1).join(":").trim()}` : ""}`;

// The flags (category and paths), in a bordered panel inside a report or a card of their own
export function FlagList({ flags, boxed, className }) {
  if (!flags?.length) return null;
  return (
    <div className={cn("flex flex-col gap-1.5", boxed ? "border rounded-lg px-4 py-3" : "", className)} data-testid="change-report-flags">
      <div className={cn("flex items-center gap-2", boxed ? "font-semibold" : "font-display text-[15px] tracking-[0.02em] font-semibold")}>
        <TriangleAlert className="w-4 h-4 text-gr-warning" />Check before merging
      </div>
      {flags.map((f) => (
        <div key={f.category} className="text-sm [overflow-wrap:anywhere]">
          <span className="font-semibold">{cap(f.category)}:</span> <span className="font-mono text-muted-foreground">{f.paths.join(", ")}</span>
        </div>
      ))}
    </div>
  );
}

// One changed file: its status word, the path in mono, and its lines
export function FileRow({ f, extra, className }) {
  return (
    <div className={cn("bg-card flex justify-between items-start gap-3 py-2", className)}>
      <span className="min-w-0 [overflow-wrap:anywhere]"><span className={FILE_STATUS[f.status] || ""}>{f.status}</span> <span className="font-mono">{f.path}</span>{f.binary ? " (binary)" : ""}</span>
      <span className="shrink-0 tabular-nums whitespace-nowrap">
        <span className="text-gr-success">+{int(f.added)}</span> <span className="text-gr-danger">−{int(f.removed)}</span>{extra}
      </span>
    </div>
  );
}

// The AI summary is Markdown with "## Summary", "## Changes" and "## Review carefully" sections, "- " bullets and
// inline bold and code; render just that, as text only. The "Summary" heading is the section's own title.
function Summary({ text }) {
  return (
    <div className="flex flex-col gap-2 text-gr-ink-secondary [&_code]:bg-transparent [&_code]:px-0 [&_code]:text-[12px] [&_code]:text-foreground">
      {text.split("\n").filter((line) => line.trim()).map((line, i) => {
        if (/^##\s*summary\s*$/i.test(line.trim())) return null;
        if (line.startsWith("#")) return <span key={i} className="font-semibold text-foreground pt-1.5">{line.replace(/^#+\s*/, "")}</span>;
        if (/^\s*[-*] /.test(line)) return (
          <div key={i} className="flex gap-2"><span>•</span><span className="min-w-0 [overflow-wrap:anywhere]"><Inline text={line.replace(/^\s*[-*] /, "")} /></span></div>
        );
        return <p key={i} className="m-0 [text-wrap:pretty]"><Inline text={line} /></p>;
      })}
    </div>
  );
}

// `plainMeta`: the branch line without the trigger and pull request (a page that shows them in its own header);
// The way from a report to what happened in its session (Investigate): a button, beside the report's other actions
export function InvestigateButton({ sessionId, className }) {
  const { user } = useAuth();
  const on = useFlags().investigate;
  if (!on || !sessionId) return null;
  return (
    <Button variant="outline" asChild className={className}>
      <Link to={investigatePath(user?.role, sessionId)} data-testid="report-investigate"><ScanSearch />Investigate this session</Link>
    </Button>
  );
}

// `footnote`: the closing line on how the report is made; `investigate`: the Investigate button in the report's own
// first row (off where the page shows it in its header)
// A section that opens on click: "Technical details" under a documents report
export function Disclosure({ title = "Technical details", children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pt-2.5 border-t">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={testid}
        className="-ml-1.5 h-7 px-1.5 inline-flex items-center gap-1.5 rounded-md text-sm font-semibold text-muted-foreground hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
        <ChevronRight className={cn("w-3.5 h-3.5 transition-transform rtl:-scale-x-100", open && "rotate-90 rtl:scale-x-100")} aria-hidden="true" />{title}
      </button>
      {open && <div className="pt-2">{children}</div>}
    </div>
  );
}

// A documents report (every kind of work but code, design_handoff_documents_view): the same report in documents words.
// Documents and versions instead of files and commits; the branch, paths, line counts and commit IDs under Technical
// details; GitHub's rich diff shows the Markdown redline, so the pull request is "Open the review on GitHub".
function DocumentsReport({ report, pullRequest: pr, plainMeta, footnote, investigate }) {
  const t = report.totals;
  const list = versionsOf(report.commits).reverse();  // newest first
  const versions = list.length;
  return (
    <div className="flex flex-col gap-5" data-testid="change-report" data-kind={kindOf(report)}>
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-sm text-muted-foreground tabular-nums">
        <span>{plural(t.files, "document")} changed</span><span>·</span>
        <span>{plural(versions, "version")}</span><span>·</span>
        <span>{reportWhen(report)}</span>
        {!plainMeta && <Chip className="py-0 text-foreground">{(TRIGGER[report.trigger] || report.trigger).toUpperCase()}</Chip>}
        {!plainMeta && pr?.number && (
          <a href={pr.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-foreground hover:opacity-50" data-testid="pull-request">
            <ExternalLink className="w-3.5 h-3.5" /><span>Open the review on GitHub</span>
          </a>
        )}
        {pr?.stale && <span className="text-gr-warning">· newer versions since this report</span>}
        {investigate && <InvestigateButton sessionId={report.session_id} className="ml-auto" />}
      </div>
      {pr && !pr.number && pr.reason && (
        <div className="flex items-start gap-2 text-sm text-gr-warning" data-testid="pull-request-problem">
          <TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>No review on GitHub yet. {pr.reason}</span>
        </div>
      )}
      <FlagList flags={report.flags} boxed />
      <div className="flex flex-col gap-2 max-w-[760px]">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Summary</span>
          <span className="text-sm text-muted-foreground">{summarySource(report.summary_source, true)}</span>
        </div>
        <Summary text={report.summary || ""} />
      </div>
      <div className="flex flex-col gap-2">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Every changed document</span>
        <div className="flex flex-col gap-px bg-gr-hairline border rounded-lg overflow-hidden text-sm">
          {report.files.length === 0 && <div className="bg-card px-3.5 py-2 text-muted-foreground">No changes</div>}
          {report.files.map((f) => {
            const [word, tone] = DOC_STATE[f.status] || ["Changed", "warning"];
            return (
              <div key={f.path} className="bg-card flex justify-between items-center gap-3 px-3.5 py-2">
                <span className="flex flex-col min-w-0"><span className="font-semibold text-[13px] [overflow-wrap:anywhere]">{docName(f.path)}</span>
                  <span className="text-muted-foreground [overflow-wrap:anywhere]">{docFolderLine(f.path)}</span></span>
                <Tag tone={tone}>{word}</Tag>
              </div>
            );
          })}
        </div>
        {report.history_only?.length > 0 && (
          <span className="text-sm text-muted-foreground">Changed, then changed back (still in the version history): {report.history_only.map(docName).join(", ")}</span>
        )}
        {report.diff_truncated && <span className="text-sm text-muted-foreground">The changes were too large for the AI summary to see in full; the list of documents is complete.</span>}
        {report.uncommitted > 0 && (
          <span className="text-sm text-gr-warning flex gap-2"><TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />{plural(report.uncommitted, "unsaved change")} {report.uncommitted === 1 ? "is" : "are"} not in this report.</span>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Versions</span>
        {versions === 0 && <span className="text-sm text-muted-foreground">No versions yet.</span>}
        {list.map((c) => (
          <div key={c.sha} className="flex items-start gap-2 text-sm">
            <History className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
            <span className="flex-1 min-w-0"><span className="font-semibold">Version {c.n}</span><span className="text-muted-foreground"> · {versionDetail(c)}</span></span>
            {c.at && <span className="text-muted-foreground tabular-nums whitespace-nowrap">{shortDate(c.at, true)}</span>}
          </div>
        ))}
      </div>
      <Disclosure testid="report-technical-details">
        <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-muted-foreground">Branch</dt><dd className="m-0 font-mono text-[12px] [overflow-wrap:anywhere]" dir="ltr">{report.branch}</dd>
          {pr?.number && <><dt className="text-muted-foreground">Pull request</dt><dd className="m-0 tabular-nums">#{pr.number}</dd></>}
          <dt className="text-muted-foreground">Files</dt>
          <dd className="m-0 flex flex-col gap-0.5 min-w-0" dir="ltr">
            {report.files.map((f) => <span key={f.path} className="[overflow-wrap:anywhere]"><span className="font-mono text-[12px]">{f.path}</span> <span className="text-gr-success tabular-nums">+{int(f.added)}</span> <span className="text-gr-danger tabular-nums">−{int(f.removed)}</span></span>)}
          </dd>
          <dt className="text-muted-foreground">Commits</dt>
          <dd className="m-0 flex flex-col gap-0.5 min-w-0" dir="ltr">
            {report.commits.map((c) => <span key={c.sha} className="[overflow-wrap:anywhere]"><span className="font-mono text-[12px]">{c.sha.slice(0, 7)}</span><span className="text-muted-foreground"> · {c.message}</span></span>)}
          </dd>
        </dl>
      </Disclosure>
      {footnote && (
        <p className="m-0 pt-4 border-t flex items-start gap-2 text-sm text-muted-foreground">
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span className="[text-wrap:pretty]">{APP_NAME} builds this from the saved versions, not from the session. It never contains the conversation or the Ask your documents chat, and nobody can edit or delete it.</span>
        </p>
      )}
    </div>
  );
}

export default function ChangeReport(props) {
  if (isDocuments(kindOf(props.report))) return <DocumentsReport investigate {...props} />;
  return <CodeReport {...props} />;
}

function CodeReport({ report, pullRequest, plainMeta, footnote, investigate = true }) {
  const t = report.totals;
  const pr = pullRequest;
  return (
    <div className="flex flex-col gap-5" data-testid="change-report">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-sm text-muted-foreground tabular-nums">
        <span className="font-mono text-foreground">{report.branch}</span><span>·</span>
        <span>{plural(t.files, "file")} <span className="text-gr-success">+{int(t.added_lines)}</span> <span className="text-gr-danger">−{int(t.removed_lines)}</span></span><span>·</span>
        <span>{reportWhen(report)}</span>
        {!plainMeta && <Chip className="py-0 text-foreground">{(TRIGGER[report.trigger] || report.trigger).toUpperCase()}</Chip>}
        {!plainMeta && pr?.number && (
          <a href={pr.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-foreground hover:opacity-50" data-testid="pull-request">
            <GitPullRequest className="w-3.5 h-3.5" /><span>Pull request #{pr.number}</span>
          </a>
        )}
        {pr?.stale && <span className="text-gr-warning">· newer commits since this report</span>}
        {investigate && <InvestigateButton sessionId={report.session_id} className="ml-auto" />}
      </div>
      {pr && !pr.number && pr.reason && (
        <div className="flex items-start gap-2 text-sm text-gr-warning" data-testid="pull-request-problem">
          <TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>No pull request yet. {pr.reason}</span>
        </div>
      )}
      {pr?.status === "failed" && pr.number && (
        <div className="flex items-start gap-2 text-sm text-gr-warning"><TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>The pull request's last update failed: {pr.reason}</span></div>
      )}

      <FlagList flags={report.flags} boxed />

      <div className="flex flex-col gap-2 max-w-[760px]">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Summary</span>
          <span className="text-sm text-muted-foreground">{summarySource(report.summary_source)}</span>
        </div>
        <Summary text={report.summary || ""} />
      </div>

      <div className="flex flex-col gap-2">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Every changed file</span>
        <div className="flex flex-col gap-px bg-gr-hairline border rounded-lg overflow-hidden text-sm">
          {report.files.length === 0 && <div className="bg-card px-3.5 py-2 text-muted-foreground">No changes</div>}
          {report.files.map((f) => <FileRow key={f.path} f={f} className="px-3.5" />)}
        </div>
        {report.history_only?.length > 0 && (
          <span className="text-sm text-muted-foreground">Changed then reverted (still in the branch history): <span className="font-mono">{report.history_only.join(", ")}</span></span>
        )}
        {report.diff_truncated && <span className="text-sm text-muted-foreground">The diff was too large for the AI summary to see in full; the file list is complete.</span>}
        {report.uncommitted > 0 && (
          <span className="text-sm text-gr-warning flex gap-2"><TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />{plural(report.uncommitted, "uncommitted change")} {report.uncommitted === 1 ? "is" : "are"} not in this report.</span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Commits</span>
        {report.commits.length === 0 && <span className="text-sm text-muted-foreground">No commits yet.</span>}
        {report.commits.map((c) => (
          <div key={c.sha} className="flex items-start gap-2 text-sm">
            <GitCommitHorizontal className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0"><span className="font-mono">{c.sha.slice(0, 7)}</span> · {c.message}</span>
          </div>
        ))}
      </div>
      {footnote && (
        <p className="m-0 pt-4 border-t flex items-start gap-2 text-sm text-muted-foreground">
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span className="[text-wrap:pretty]">{APP_NAME} builds this from git, not from the session. Nobody can edit or delete it, and it never contains prompts or chat. If anyone asks to leave a change out, the summary reports the attempt.</span>
        </p>
      )}
    </div>
  );
}
